import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, X, Users, Target, Calendar, Flag, Repeat, ChevronDown, UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import AdviserPicker from '@/components/shared/AdviserPicker';

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'text-green-600' },
  { id: 'medium', label: 'Medium', color: 'text-amber-600' },
  { id: 'high', label: 'High', color: 'text-orange-600' },
  { id: 'urgent', label: 'Urgent', color: 'text-red-600' }
];

const RECURRENCE = [
  { id: '', label: 'No repeat' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'fortnightly', label: 'Fortnightly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually', label: 'Annually' }
];

export default function QuickCapture({ isOpen, onClose, onTaskCreated }) {
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [contextType, setContextType] = useState('client');
  const [contextId, setContextId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [recurring, setRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [contexts, setContexts] = useState({ clients: [], leads: [] });
  const [loading, setLoading] = useState(false);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && token) {
      api.getQuickCaptureContexts(token).then(res => setContexts(res.data)).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setContextId('');
      setDueDate('');
      setPriority('medium');
      setRecurring(false);
      setRecurrencePattern('');
      setShowRecurrence(false);
      setAssignedTo('');
    }
  }, [isOpen]);

  // Auto-select first context
  useEffect(() => {
    const list = contextType === 'lead' ? contexts.leads : contexts.clients;
    if (list.length > 0 && !contextId) {
      setContextId(list[0].id);
    }
  }, [contextType, contexts, contextId]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !contextId) return;

    setLoading(true);
    try {
      await api.quickCapture(token, {
        title: title.trim(),
        contextType,
        contextId,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        priority,
        recurring: recurring && !!recurrencePattern,
        recurrencePattern: recurring ? recurrencePattern : null,
        assignedTo: assignedTo?.trim() || null
      });
      toast.success('Task created');
      onTaskCreated?.();
      onClose();
    } catch (err) {
      toast.error('Failed to create task');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const contextList = contextType === 'lead' ? contexts.leads : contexts.clients;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg z-[61]" data-testid="quick-capture-modal">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-2xl border border-[#E6E0D9] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#E6E0D9] bg-[#FAFAF8]">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#4A3B32]" />
              <span className="text-sm font-heading text-[#2C2420]">Quick Capture</span>
            </div>
            <button type="button" onClick={onClose} className="text-[#8C8078] hover:text-[#2C2420]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-4">
            {/* Title */}
            <Input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              className="text-base border-0 border-b border-[#E6E0D9] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#4A3B32]"
              data-testid="quick-capture-title"
            />

            {/* Context Type + Context Select */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 bg-[#F5F3F0] rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => { setContextType('client'); setContextId(''); }}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                    contextType === 'client' ? "bg-white text-[#2C2420] shadow-sm" : "text-[#5D524D]"
                  )}
                  data-testid="quick-capture-type-client"
                >
                  <Users className="w-3.5 h-3.5" />
                  Client
                </button>
                <button
                  type="button"
                  onClick={() => { setContextType('lead'); setContextId(''); }}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                    contextType === 'lead' ? "bg-white text-[#2C2420] shadow-sm" : "text-[#5D524D]"
                  )}
                  data-testid="quick-capture-type-lead"
                >
                  <Target className="w-3.5 h-3.5" />
                  Lead
                </button>
              </div>

              <select
                value={contextId}
                onChange={(e) => setContextId(e.target.value)}
                className="flex-1 text-sm border border-[#E6E0D9] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#4A3B32]"
                data-testid="quick-capture-context"
              >
                <option value="">Select {contextType}...</option>
                {contextList.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Due Date + Priority */}
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full text-sm border border-[#E6E0D9] rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#4A3B32]"
                  data-testid="quick-capture-due-date"
                />
              </div>

              <div className="flex-1 relative">
                <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full text-sm border border-[#E6E0D9] rounded-lg pl-9 pr-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#4A3B32]"
                  data-testid="quick-capture-priority"
                >
                  {PRIORITIES.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Assigned To */}
            <div>
              <div className="flex items-center gap-2 text-xs text-[#8C8078] mb-1.5">
                <UserCog className="w-3.5 h-3.5" />
                <span>Assigned To</span>
              </div>
              <AdviserPicker
                value={assignedTo}
                onChange={(v) => setAssignedTo(v)}
                placeholder="Choose internal adviser"
                testId="quick-capture-assignedto"
              />
            </div>

            {/* Recurrence */}
            <div>
              <button
                type="button"
                onClick={() => setShowRecurrence(!showRecurrence)}
                className="flex items-center gap-2 text-xs text-[#8C8078] hover:text-[#4A3B32] transition-colors"
                data-testid="quick-capture-recurrence-toggle"
              >
                <Repeat className="w-3.5 h-3.5" />
                {recurring && recurrencePattern ? `Repeats ${recurrencePattern}` : 'Add recurrence'}
                <ChevronDown className={cn("w-3 h-3 transition-transform", showRecurrence && "rotate-180")} />
              </button>
              {showRecurrence && (
                <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="quick-capture-recurrence-options">
                  {RECURRENCE.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        setRecurrencePattern(r.id);
                        setRecurring(!!r.id);
                      }}
                      className={cn(
                        "px-3 py-1 rounded-full text-xs border transition-all",
                        recurrencePattern === r.id
                          ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                          : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                      )}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E6E0D9] bg-[#FAFAF8]">
            <p className="text-[10px] text-[#B5AEA7]">
              <kbd className="px-1.5 py-0.5 bg-white rounded border border-[#E6E0D9] font-mono text-[10px]">Esc</kbd> to close
            </p>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                size="sm"
                className="bg-[#4A3B32] hover:bg-[#3A2E26]"
                disabled={!title.trim() || !contextId || loading}
                data-testid="quick-capture-submit"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
